import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrganization,
  fetchOrganization,
  fetchOrganizationAllowances,
  fetchOrganizationFilterOptions,
  fetchOrganizations,
  updateOrganization,
  updateOrganizationCreditSettings,
  updateOrganizationSettings,
} from "@/api/organizations";
import type {
  OrgCreditSettingsPatch,
  OrgListParams,
  OrgPatch,
  OrgSettingsPatch,
} from "@/interfaces/organization";

export function useOrganizations(
  params?: OrgListParams & {
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page?: number;
    limit?: number;
    /** Set to false to skip the query (e.g. when the org is already known). Default: true. */
    enabled?: boolean;
  },
) {
  const { enabled = true, ...queryParams } = params ?? {};
  return useQuery({
    queryKey: ["organizations", queryParams],
    queryFn: () => fetchOrganizations(queryParams),
    enabled,
  });
}

/** Options for the Organization filter field (D4/D3), gated by the caller (view ORGANIZATION). */
export function useOrganizationFilterOptions(enabled = true) {
  return useQuery({
    queryKey: ["organizations", "filter-options"],
    queryFn: fetchOrganizationFilterOptions,
    enabled,
  });
}

/** Fetches a single organization by id (e.g. the caller's own org for the Account view). */
export function useOrganization(id: number | null | undefined) {
  return useQuery({
    queryKey: ["organizations", "detail", id],
    queryFn: () => fetchOrganization(id as number),
    enabled: id != null,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => createOrganization(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: OrgPatch }) => updateOrganization(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

/** GFG-only org settings (account lead / lifecycle / provisional credit limit) → /settings route. */
export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: OrgSettingsPatch }) =>
      updateOrganizationSettings(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
}

/** The org's credit policy — its own endpoint and its own capability (edit CREDIT @ all). */
export function useUpdateOrganizationCreditSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: OrgCreditSettingsPatch }) =>
      updateOrganizationCreditSettings(id, patch),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organization-allowances", id] });
    },
  });
}

/** The Allowances tab. */
export function useOrgAllowances(id: number) {
  return useQuery({
    queryKey: ["organization-allowances", id],
    queryFn: () => fetchOrganizationAllowances(id),
  });
}
